
import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToSection } from "@/utils/scrollUtils";
import { useEffect, useRef } from "react";

export const Hero = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Add scroll-triggered animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden noise-overlay">
      {/* Mesh Gradient Background */}
      <div className="absolute inset-0 bg-background mesh-gradient-1" />

      {/* Geometric Grid Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50" />

      {/* Decorative Elements */}
      <div className="absolute top-20 right-[10%] w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[120px] animate-pulse-glow" />
      <div className="absolute bottom-20 left-[5%] w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[100px] animate-pulse-glow delay-500" />

      {/* Diagonal Accent Lines */}
      <div className="absolute top-1/4 right-1/4 w-px h-[300px] bg-gradient-to-b from-transparent via-amber-500/30 to-transparent rotate-45 hidden lg:block" />
      <div className="absolute bottom-1/3 left-1/3 w-px h-[200px] bg-gradient-to-b from-transparent via-cyan-500/20 to-transparent -rotate-45 hidden lg:block" />

      {/* Corner Accents */}
      <div className="absolute top-32 left-8 w-20 h-20 border-l border-t border-amber-500/20 hidden lg:block" />
      <div className="absolute bottom-32 right-8 w-20 h-20 border-r border-b border-amber-500/20 hidden lg:block" />

      <div className="container mx-auto px-6 relative z-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-7 xl:col-span-6">
            {/* Label */}
            <div className="animate-hero-fade-up">
              <span className="label-mono text-amber-400 tracking-widest">
                Enterprise Technology Partners
              </span>
            </div>

            {/* Main Heading - Oversized Display Typography */}
            <h1 className="mt-6 animate-hero-fade-up delay-100">
              <span className="display-xl text-white block">
                We Build
              </span>
              <span className="display-xl text-gradient-gold block mt-2">
                What Others
              </span>
              <span className="display-xl text-white block mt-2">
                Can't.
              </span>
            </h1>

            {/* Subheading */}
            <p className="mt-8 text-xl md:text-2xl text-zinc-400 max-w-xl leading-relaxed animate-hero-fade-up delay-300">
              From AI-powered automation to seamless enterprise integrations—we architect solutions that
              <span className="text-white font-medium"> define industries</span>.
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row items-start gap-4 animate-hero-fade-up delay-400">
              <Button
                size="lg"
                className="btn-primary h-14 px-8 rounded-full text-lg group"
                onClick={() => scrollToSection('contact')}
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>

              <Button
                variant="ghost"
                size="lg"
                className="btn-outline-glow h-14 px-8 rounded-full text-lg"
                onClick={() => scrollToSection('portfolio')}
              >
                View Case Studies
              </Button>
            </div>

            {/* Trust Signal */}
            <div className="mt-12 animate-hero-fade-up delay-500">
              <div className="inline-flex items-center gap-3 glass-panel-warm rounded-full px-5 py-3">
                <div className="relative">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                  <div className="absolute inset-0 w-2 h-2 bg-emerald-400 rounded-full animate-ping" />
                </div>
                <span className="text-sm text-zinc-300">
                  Talk to our clients first — <span className="text-amber-400 font-medium">start only when satisfied</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Stats & Experience */}
          <div className="lg:col-span-5 xl:col-span-6 lg:pl-8">
            <div className="relative">
              {/* Experience Card - Overlapping Design */}
              <div className="card-featured p-8 md:p-10 animate-hero-fade-up delay-200">
                <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

                <div className="space-y-8">
                  {/* Main Message */}
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gradient-gold">
                      Deep Expertise
                    </h2>
                    <p className="mt-4 text-lg text-zinc-400">Senior professionals with real-world experience</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent" />

                  {/* Description */}
                  <p className="text-lg text-zinc-300 leading-relaxed">
                    Our team brings extensive hands-on experience in enterprise systems. We don't just write code—we
                    <span className="text-white font-medium"> architect solutions that drive results</span>.
                  </p>

                  {/* Value Props */}
                  <div className="grid grid-cols-1 gap-3 pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <span className="text-zinc-300">Enterprise-grade implementations</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <span className="text-zinc-300">Complex system integrations</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <span className="text-zinc-300">Dedicated project support</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Badge - Offset/Overlapping */}
              <div className="absolute -bottom-4 -left-4 md:-left-8 glass-panel rounded-xl p-4 animate-hero-fade-up delay-600 hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                    <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Our Expertise</div>
                    <div className="text-xs text-zinc-400">ERP • eCommerce • Integrations</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-hero-fade-in delay-800">
        <button
          onClick={() => scrollToSection('services')}
          className="flex flex-col items-center gap-2 text-zinc-500 hover:text-amber-400 transition-colors group"
        >
          <span className="text-xs label-mono tracking-widest">Explore</span>
          <ChevronDown className="w-5 h-5 animate-scroll-indicator" />
        </button>
      </div>
    </section>
  );
};
